// @ts-nocheck
// oxfmt-ignore
import type { CustomThemeConfig } from "./types";
import { componentTokens } from "../tokens/components.tokens";
import { tokens } from "../tokens/tokens";

const tokenPaletteColors = {};

const paletteColorNames = new Set([
  "blue",
  "magenta",
  "red",
  "volcano",
  "orange",
  "gold",
  "lime",
  "green",
  "cyan",
  "geekblue",
  "purple",
  "gray",
  "grey",
  "offwhite",
  "yellow",
  "teal",
  "indigo",
  "pink",
  "brown",
  "black",
  "white",
  "primary",
  "secondary",
  "success",
  "warning",
  "error",
  "info",
]);

for (const color in tokens) {
  if (!paletteColorNames.has(color) && !color.startsWith("color")) continue;
  if (typeof tokens[color] !== "object" || tokens[color] === null) continue;

  if (typeof tokens[color].value === "string") {
    tokenPaletteColors[color] = tokens[color].value;
    continue;
  }

  for (const shade in tokens[color]) {
    const token = tokens[color][shade];
    if (!token?.value) continue;

    const value = token.value;

    tokenPaletteColors[`${color}.${shade}`] = value;

    // add e.g blue1 key as well but only for palette colors that have shades
    if (paletteColorNames.has(color)) {
      tokenPaletteColors[`${color}${shade}`] = value;
    }
  }
}

// Color Primary
const colorPrimaryLight = "#0d058f";

const fontFamily = {
  fontFamily: "Space Grotesk Variable",
  fontFamilyCode:
    "Menlo, Consolas, Monaco, Liberation Mono, Lucida Console, monospace;",
};

const typographyBaseStyles = {
  titleMarginBottom: 0,
  titleMarginTop: 0,
};

const layoutLightTheme = {
  bodyBg: "#ffffff",
  footerBg: "#f2ece6",
  headerBg: "#ffffff",
  lightSiderBg: "#f6f2f0",
  siderBg: "#001529",
  triggerBg: "#002140",
};

const tableThemeLight = {
  headerBg: "#f7f4f1",
  footerBg: "rgba(0, 0, 0, 0.02)",
  rowSelectedHoverBg: "#e4e1ed",
  headerFilterHoverBg: "#f6f4ff",
  headerBorderRadius: 4, // default 8
  headerSortActiveBg: "#f6f4ff", // default #f0f0f0
  headerSortHoverBg: "#f6f4ff",
  cellwhiteBg: "#fafafa",
  cellgreyBg: "rgba(0, 0, 0, 0.02)",
};

const lineWidths = {
  lineWidth: tokens.lineWidth?.value ?? 1,
  lineWidthBold: tokens.lineWidthBold?.value ?? 2,
  lineWidthFocus: tokens.lineWidthFocus?.value ?? 4,
};

const controlSettings = {
  controlItemBgActive: tokens.controlItemBgActive?.value ?? "#eeedf6",
  controlItemBgActiveDisabled:
    tokens.controlItemBgActiveDisabled?.value ?? "#f7f4f1",
  controlItemBgActiveHover: tokens.controlItemBgActiveHover?.value ?? "#e4e1ed",
  controlItemBgHover: tokens.controlItemBgHover?.value ?? "rgba(0, 0, 0, 0.04)",
  controlInteractiveSize: tokens.controlInteractiveSize?.value ?? 16,
  controlPaddingHorizontal: tokens.controlPaddingHorizontal?.value ?? 12,
  controlPaddingHorizontalSM: tokens.controlPaddingHorizontalSM?.value ?? 8,
};

const tokenTextColorsLight = {
  colorFillAlterSolid: tokenPaletteColors["offwhite.1"] || "#fafafa",
  colorFilledHandleBg: tokenPaletteColors["offwhite.3"] || "#f0f0f0",
  color: tokenPaletteColors["color 2"] || "#ffffff",
  colorTextBase: tokenPaletteColors["color 2"] || "#ffffff",
  colorText: "rgba(0, 0, 0, 0.88)",
  colorTextSecondary: "rgba(0, 0, 0, 0.65)",
  colorTextTertiary: "rgba(0, 0, 0, 0.7)",
  colorTextQuaternary: "rgba(0, 0, 0, 0.22)",
  colorTextLightSolid: tokenPaletteColors["color 2"] || "#ffffff",
  colorTextHeading: "rgba(0, 0, 0, 0.88)",
  colorTextLabel: "rgba(0, 0, 0, 0.65)",
  colorTextDescription: "rgba(0, 0, 0, 0.7)",
  colorTextDisabled: "rgba(0, 0, 0, 0.22)",
  colorTextPlaceholder: "rgba(0, 0, 0, 0.22)",
  colorIcon: "rgba(0, 0, 0, 0.65)",
  colorIconHover: "rgba(0, 0, 0, 0.88)",
  colorBgContainer: tokenPaletteColors["color 2"] || "#ffffff",
  colorBgElevated: tokenPaletteColors["color 2"] || "#ffffff",
  colorBgLayout: tokenPaletteColors["offwhite.3"] || "#f2ece6",
  colorBgMask: "rgba(0, 0, 0, 0.45)",
  colorBgSpotlight: "rgba(0, 0, 0, 0.85)",
  colorBgOverlay: "rgba(0, 0, 0, .5)",
  colorBorder: tokenPaletteColors["offwhite.2"] || "#cdcaca",
  colorBorderSecondary: tokenPaletteColors["offwhite.3"] || "#f0f0f0",
  colorFill: tokenPaletteColors["offwhite.1"] || "#f7f4f1",
  colorFillSecondary: tokenPaletteColors["offwhite.3"] || "#f6f4ff",
  colorFillTertiary: "rgba(0, 0, 0, 0.04)",
  colorFillQuaternary: "rgba(0, 0, 0, 0.02)",
  colorWhite: tokenPaletteColors["color 2"] || "#ffffff",
  colorBgBase: tokenPaletteColors["color 2"] || "#ffffff",
  colorBgContainerDisabled: "rgba(0, 0, 0, 0.04)",
  colorBgTextActive: tokenPaletteColors["offwhite.1"] || "#f7f4f1",
  colorBgTextHover: tokenPaletteColors["offwhite.3"] || "#f6f4ff",
  colorBorderBg: tokenPaletteColors["color 2"] || "#ffffff",
  colorFillContent: tokenPaletteColors["offwhite.3"] || "#f6f4ff",
  colorFillContentHover: tokenPaletteColors["offwhite.1"] || "#f7f4f1",
  colorFillAlter: "rgba(0, 0, 0, 0.02)",
  transparent: "rgba(0, 0, 0, 0)",
  colorSplit: "rgba(0, 0, 0, 0.06)",
  colorPrimary: colorPrimaryLight,
  colorSuccess: tokenPaletteColors["green.6"] || "#52c41a",
  colorWarning: tokenPaletteColors["gold.6"] || "#faad14",
  colorInfo: colorPrimaryLight,
  colorError: tokenPaletteColors["red.5"] || "#ff4d4f",
  colorLink: colorPrimaryLight,
  colorErrorBg: tokenPaletteColors["red.1"] || "#fff2f0",
  colorErrorBgHover: tokenPaletteColors["red.1"] || "#fff1f0",
  colorErrorBorder: tokenPaletteColors["red.2"] || "#ffccc7",
  colorErrorBorderHover: tokenPaletteColors["red.3"] || "#ffa39e",
  colorErrorHover: tokenPaletteColors["red.4"] || "#ff7875",
  colorErrorActive: tokenPaletteColors["red.7"] || "#d9363e",
  colorErrorTextHover: tokenPaletteColors["red.4"] || "#ff7875",
  colorErrorText: tokenPaletteColors["red.5"] || "#ff4d4f",
  colorErrorTextActive: tokenPaletteColors["red.7"] || "#cf1322",
  colorLinkHover: tokenPaletteColors["blue.5"] || "#4439a3",
  colorInfoBg: tokenPaletteColors["blue.1"] || "#eeedf6",
  colorInfoBgHover: tokenPaletteColors["blue.1"] || "#eeedf6",
  colorInfoBorder: tokenPaletteColors["blue.2"] || "#e4e1ed",
  colorInfoBorderHover: tokenPaletteColors["blue.4"] || "#655ab0",
  colorInfoHover: tokenPaletteColors["blue.5"] || "#4439a3",
  colorInfoActive: tokenPaletteColors["blue.7"] || "#08047d",
  colorInfoTextHover: tokenPaletteColors["blue.5"] || "#4439a3",
  colorInfoText: colorPrimaryLight,
  colorInfoTextActive: tokenPaletteColors["blue.7"] || "#08047d",
  colorLinkActive: tokenPaletteColors["blue.7"] || "#08047d",
  colorPrimaryBg: tokenPaletteColors["blue.1"] || "#eeedf6",
  colorPrimaryBgHover: tokenPaletteColors["blue.1"] || "#eeedf6",
  colorPrimaryBorder: tokenPaletteColors["blue.2"] || "#e4e1ed",
  colorPrimaryBorderHover: tokenPaletteColors["blue.4"] || "#655ab0",
  colorPrimaryHover: tokenPaletteColors["blue.5"] || "#4439a3",
  colorPrimaryActive: tokenPaletteColors["blue.7"] || "#08047d",
  colorPrimaryTextHover: tokenPaletteColors["blue.5"] || "#4439a3",
  colorPrimaryText: colorPrimaryLight,
  colorPrimaryTextActive: tokenPaletteColors["blue.7"] || "#08047d",
  colorSuccessBg: tokenPaletteColors["green.1"] || "#f0ffe1",
  colorSuccessBgHover: tokenPaletteColors["green.2"] || "#d9f7be",
  colorSuccessBorder: tokenPaletteColors["green.3"] || "#b7eb8f",
  colorSuccessBorderHover: tokenPaletteColors["green.4"] || "#95de64",
  colorSuccessHover: tokenPaletteColors["green.4"] || "#95de64",
  colorSuccessActive: tokenPaletteColors["green.7"] || "#389e0d",
  colorSuccessTextHover: tokenPaletteColors["green.5"] || "#73d13d",
  colorSuccessText: tokenPaletteColors["green.6"] || "#52c41a",
  colorSuccessTextActive: tokenPaletteColors["green.9"] || "#135200",
  colorWarningBg: tokenPaletteColors["gold.1"] || "#fff8d4",
  colorWarningBgHover: tokenPaletteColors["gold.2"] || "#fff1b8",
  colorWarningBorder: tokenPaletteColors["gold.3"] || "#ffe58f",
  colorWarningBorderHover: tokenPaletteColors["gold.4"] || "#ffd666",
  colorWarningHover: tokenPaletteColors["gold.4"] || "#ffd666",
  colorWarningActive: tokenPaletteColors["gold.7"] || "#d48806",
  colorWarningTextHover: tokenPaletteColors["gold.5"] || "#ffc53d",
  colorWarningText: tokenPaletteColors["gold.8"] || "#b05800",
  colorWarningTextActive: tokenPaletteColors["gold.8"] || "#b05800",
};

const borderRadiuses = {
  borderRadius: tokens.borderRadius?.value ?? 4,
  borderRadiusLG: tokens.borderRadiusLG?.value ?? 4,
  borderRadiusSM: tokens.borderRadiusSM?.value ?? 2,
  borderRadiusXS: tokens.borderRadiusXS?.value ?? 0,
};

const themeSizes = {
  themeSize: 16,
  themeSizeLG: 24,
  themeSizeMD: 20,
  themeSizeSM: 12,
  themeSizeXS: 8,
  themeSizeXXS: 4,
  themeSizeXL: 32,
  themeSizeXXL: 48,
};

const controlHeights = {
  controlHeight: tokens.controlHeight?.value ?? 32,
  controlHeightLG: tokens.controlHeightLG?.value ?? 40,
  controlHeightSM: tokens.controlHeightSM?.value ?? 24,
  controlHeightXS: tokens.controlHeightXS?.value ?? 16,
};

const otherSizes = {
  inputFontSize: tokens.inputFontSize?.value ?? 14,
  inputFontSizeLG: tokens.inputFontSizeLG?.value ?? 16,
  inputFontSizeSM: tokens.inputFontSizeSM?.value ?? 12,
  multipleItemHeight: tokens.multipleItemHeight?.value ?? 32,
  multipleItemHeightLG: tokens.multipleItemHeightLG?.value ?? 40,
  multipleItemHeightSM: tokens.multipleItemHeightSM?.value ?? 24,
  textHeight: tokens.textHeight?.value ?? 40,
  withoutTimeCellHeight: tokens.withoutTimeCellHeight?.value ?? 66,
  presetsMaxWidth: tokens.presetsMaxWidth?.value ?? 200,
  presetsWidth: tokens.presetsWidth?.value ?? 120,
};

const screenSizes = {
  screenLG: tokens.screenLG?.value ?? 992,
  screenLGMax: tokens.screenLGMax?.value ?? 1199,
  screenLGMin: tokens.screenLGMin?.value ?? 992,
  screenMD: tokens.screenMD?.value ?? 768,
  screenMDMax: tokens.screenMDMax?.value ?? 991,
  screenMDMin: tokens.screenMDMin?.value ?? 768,
  screenSM: tokens.screenSM?.value ?? 576,
  screenSMMax: tokens.screenSMMax?.value ?? 767,
  screenSMMin: tokens.screenSMMin?.value ?? 576,
  screenXL: tokens.screenXL?.value ?? 1200,
  screenXLMax: tokens.screenXLMax?.value ?? 1599,
  screenXLMin: tokens.screenXLMin?.value ?? 1200,
  screenXS: tokens.screenXS?.value ?? 480,
  screenXSMax: tokens.screenXSMax?.value ?? 575,
  screenXSMin: tokens.screenXSMin?.value ?? 480,
  screenXXL: tokens.screenXXL?.value ?? 1600,
  screenXXLMin: tokens.screenXXLMin?.value ?? 1600,
};

const margins = {
  margin: tokens.margin?.value ?? 16,
  marginLG: tokens.marginLG?.value ?? 24,
  marginMD: tokens.marginMD?.value ?? 20,
  marginSM: tokens.marginSM?.value ?? 12,
  marginXL: tokens.marginXL?.value ?? 32,
  marginXS: tokens.marginXS?.value ?? 8,
  marginXXL: tokens.marginXXL?.value ?? 48,
  marginXXS: tokens.marginXXS?.value ?? 4,
};

const paddings = {
  padding: tokens.padding?.value ?? 16,
  paddingLG: tokens.paddingLG?.value ?? 24,
  paddingMD: tokens.paddingMD?.value ?? 20,
  paddingSM: tokens.paddingSM?.value ?? 12,
  paddingXL: tokens.paddingXL?.value ?? 32,
  paddingXS: tokens.paddingXS?.value ?? 8,
  paddingXXS: tokens.paddingXXS?.value ?? 4,
};

const paddingContentSpacings = {
  paddingContentHorizontal: tokens.paddingContentHorizontal?.value ?? 16,
  paddingContentHorizontalLG: tokens.paddingContentHorizontalLG?.value ?? 24,
  paddingContentHorizontalSM: tokens.paddingContentHorizontalSM?.value ?? 16,
  paddingContentVertical: tokens.paddingContentVertical?.value ?? 12,
  paddingContentVerticalLG: tokens.paddingContentVerticalLG?.value ?? 16,
  paddingContentVerticalSM: tokens.paddingContentVerticalSM?.value ?? 8,
};

const fontSizes = {
  fontSize: tokens.fontSize?.value ?? 14,
  fontSizeLG: tokens.fontSizeLG?.value ?? 16,
  fontSizeSM: tokens.fontSizeSM?.value ?? 12,
  fontSizeXL: tokens.fontSizeXL?.value ?? 20,
  fontSizeHeading1: tokens.fontSizeHeading1?.value ?? 38,
  fontSizeHeading2: tokens.fontSizeHeading2?.value ?? 30,
  fontSizeHeading3: tokens.fontSizeHeading3?.value ?? 24,
  fontSizeHeading4: tokens.fontSizeHeading4?.value ?? 20,
  fontSizeHeading5: tokens.fontSizeHeading5?.value ?? 16,
  fontSizeIcon: tokens.fontSizeIcon?.value ?? 12,
};

const lineHeights = {
  lineHeight: tokens.lineHeight?.value ?? 1.5714285714285714,
  lineHeightHeading1: tokens.lineHeightHeading1?.value ?? 1.2105263157894737,
  lineHeightHeading2: tokens.lineHeightHeading2?.value ?? 1.2666666666666666,
  lineHeightHeading3: tokens.lineHeightHeading3?.value ?? 1.3333333333333333,
  lineHeightHeading4: tokens.lineHeightHeading4?.value ?? 1.4,
  lineHeightHeading5: tokens.lineHeightHeading5?.value ?? 1.5,
  lineHeightLG: tokens.lineHeightLG?.value ?? 1.5,
  lineHeightSM: tokens.lineHeightSM?.value ?? 1.6666666666666667,
};

const fontWeights = {
  fontWeightStrong: tokens.fontWeightStrong?.value ?? 600,
  fontWeightNormal: tokens.fontWeightNormal?.value ?? 400,
};

const baseToken = {
  ...controlSettings,
  ...borderRadiuses,
  ...themeSizes,
  ...controlHeights,
  ...lineWidths,
  ...otherSizes,
  ...screenSizes,
  ...margins,
  ...paddings,
  ...paddingContentSpacings,
  ...fontFamily,
  ...fontSizes,
  ...lineHeights,
  ...fontWeights,
};

export const theme: CustomThemeConfig = {
  // Enables css variables - https://ant.design/docs/react/css-variables#api
  cssVar: {
    prefix: "ant",
  },
  // Global Token
  token: {
    ...tokenPaletteColors,
    ...tokenTextColorsLight,
    ...baseToken,
  },
  // Design Token
  components: {
    Input: {
      ...componentTokens.Input,
      ...fontFamily,
      controlOutlineWidth: 1,
    },
    Segmented: {
      ...componentTokens.Segmented,
      ...fontFamily,
    },
    TimePicker: {
      ...componentTokens.TimePicker,
      ...fontFamily,
    },
    Timeline: {
      ...componentTokens.Timeline,
      ...fontFamily,
    },
    Tabs: {
      ...componentTokens.Tabs,
      ...fontFamily,
    },
    Table: tableThemeLight,
    Steps: {
      ...componentTokens.Steps,
      ...fontFamily,
    },
    Slider: {
      ...componentTokens.Slider,
      ...fontFamily,
    },
    Radio: {
      ...componentTokens.Radio,
      ...fontFamily,
    },
    Menu: {
      ...componentTokens.Menu,
      ...fontFamily,
    },
    InputNumber: {
      ...componentTokens.InputNumber,
      ...fontFamily,
      controlOutlineWidth: 1,
    },
    Cascader: {
      ...componentTokens.Cascader,
      ...fontFamily,
    },
    Calendar: {
      ...componentTokens.Calendar,
      ...fontFamily,
    },
    Button: {
      ...componentTokens.Button,
      ...fontFamily,
    },
    Tour: {
      ...componentTokens.Tour,
      ...fontFamily,
    },
    Typography: {
      ...componentTokens.Typography,
      ...fontFamily,
      ...typographyBaseStyles,
    },
    TreeSelect: {
      ...componentTokens.TreeSelect,
      ...fontFamily,
    },
    Tree: {
      ...componentTokens.Tree,
      ...fontFamily,
    },
    Tag: {
      ...componentTokens.Tag,
      ...fontFamily,
    },
    Select: {
      ...componentTokens.Select,
      ...fontFamily,
      controlOutlineWidth: 1,
    },
    Progress: {
      ...componentTokens.Progress,
      ...fontFamily,
    },
    Pagination: {
      ...componentTokens.Pagination,
      ...fontFamily,
    },
    Modal: {
      ...componentTokens.Modal,
      ...fontFamily,
    },
    Message: {
      ...componentTokens.Message,
      ...fontFamily,
    },
    List: {
      ...componentTokens.List,
      ...fontFamily,
    },
    Dropdown: {
      ...componentTokens.Dropdown,
      ...fontFamily,
    },
    DatePicker: {
      ...componentTokens.DatePicker,
      ...fontFamily,
      controlOutlineWidth: 1,
    },
    Breadcrumb: {
      ...componentTokens.Breadcrumb,
      ...fontFamily,
    },
    Layout: layoutLightTheme,
  },
};
